import { GridCellParams } from '@mui/x-data-grid-pro';
import { GroupedGridColDef } from '../../Compounds/UpstackDataGrid/gridColumnTypes';
import { dateColumnOperators, standardOperators, tagsOperators } from '../../Compounds/UpstackDataGrid/helpers';
import { formatDate } from '../../../utils/helpers';
import { FullPageCTARenderer } from '../../Compounds/UpstackDataGrid/CellRenderers/FullPageCTARenderer';
import { EntityTagRenderer } from '../../Compounds/UpstackDataGrid/CellRenderers/EntityTagRenderer';
import { SupportedTagEntityTypes } from '../../../store/tagEntityTypes';
import { addressComponentColumns } from '../../Compounds/UpstackDataGrid/addressColumns';
import { tagsColumn } from '../../Compounds/UpstackDataGrid/tagColumns';
import type { SupportedTagEntities } from '../../../store/Tags';
import type { Project } from '../../../store/Project';
import { getDefaultProjectAssetDetailFieldDefs } from './ProjectsRelatedInventoryGridColumnDefs';

// Lazy on purpose: a static import closes the col-def ↔ store/User cycle and hands out a half-built store.
const refetchProject = async (id: string): Promise<void> =>
  (await import('../../../store/Project')).fetchProjectById(id);

export const projectRowAccountId = (row: SupportedTagEntities): string | undefined => (row as Project).account?.id;

/**
 * Canonical list of Projects Main Table grid columns. The `field` of each entry is the
 * stable id used by:
 *  - The UI's column visibility/disable toggles
 *  - `AccountCustomization#content.project_grid.disabledFields` in upstack_api
 *  - The MCP customization tools' `tools/list` input schemas
 *
 * Cross-repo coupling: when adding, renaming, or removing a column here, mirror the
 * change in upstack_api at `app/models/account_customization.rb`
 * (`AccountCustomization::PROJECT_GRID_FIELDS`).
 *
 * Exception: the address-component columns (spread in below) are off-by-default extras
 * and intentionally NOT mirrored in PROJECT_GRID_FIELDS (not account-customizable).
 */
export const DefaultProjectGridColumnsDefs: GroupedGridColDef[] = [
  {
    field: 'details',
    headerName: 'Details',
    sortable: false,
    width: 65,
    filterable: false,
    type: 'singleSelect',
    valueOptions: [],
    renderCell: (params: GridCellParams) => (
      <FullPageCTARenderer
        data={params}
        value={''}
        valueFormatted={''}
      />
    ),
    hideable: false,
    disableReorder: true
  },
  {
    field: 'name',
    headerName: 'Project Name',
    sortable: true,
    width: 200,
    filterable: true,
    type: 'singleSelect',
    valueOptions: [],
    filterOperators: standardOperators(),
    description: 'Drag to move column'
  },
  {
    field: 'siteName',
    headerName: 'Site Name',
    sortable: true,
    width: 200,
    filterable: true,
    type: 'singleSelect',
    valueOptions: [],
    filterOperators: standardOperators(),
    description: 'Drag to move column'
  },
  {
    field: 'supplier.name',
    headerName: 'Supplier',
    sortable: true,
    width: 250,
    filterable: true,
    valueGetter: (value, row) => row?.supplier?.name,
    type: 'singleSelect',
    valueOptions: [],
    filterOperators: standardOperators(),
    description: 'Drag to move column'
  },
  {
    field: 'product.nameAbbreviation',
    headerName: 'Product',
    sortable: true,
    width: 200,
    filterable: true,
    valueGetter: (value, row) => row?.product?.nameAbbreviation || row?.product?.name,
    valueFormatter: (value, row) => row?.product?.nameAbbreviation || row?.product?.name,
    type: 'singleSelect',
    valueOptions: [],
    filterOperators: standardOperators(),
    description: 'Drag to move column'
  },
  {
    field: 'status',
    headerName: 'Status',
    sortable: true,
    width: 200,
    filterable: true,
    type: 'singleSelect',
    valueOptions: [],
    filterOperators: standardOperators(),
    description: 'Drag to move column'
  },
  {
    field: 'nextSteps',
    headerName: 'Next Steps',
    sortable: true,
    width: 250,
    filterable: true,
    type: 'singleSelect',
    valueOptions: [],
    filterOperators: standardOperators(),
    description: 'Drag to move column'
  },
  {
    field: 'projectStartDate',
    headerName: 'Project Start Date',
    sortable: true,
    valueFormatter: (value) => formatDate(value),
    width: 200,
    filterable: true,
    type: 'singleSelect',
    valueOptions: [],
    filterOperators: dateColumnOperators(),
    description: 'Drag to move column'
  },
  {
    field: 'serviceCount',
    headerName: 'Service Count',
    sortable: true,
    valueGetter: (value, row) => row?.serviceCount ?? null,
    width: 150,
    filterable: true,
    type: 'singleSelect',
    valueOptions: [],
    valueOptionsComparator: (a, b) => Number(a) - Number(b),
    filterOperators: standardOperators(),
    description: 'Drag to move column',
    defaultHidden: true
  },
  ...addressComponentColumns(standardOperators()),
  tagsColumn({
    filterOperators: tagsOperators(),
    renderer: EntityTagRenderer,
    entityType: SupportedTagEntityTypes.PROJECT,
    entityAccountId: projectRowAccountId,
    refetch: refetchProject,
    width: 500
  })
];

// Project Name ships with the projects_updates redesign; strip it while the flag is off.
export const projectGridColumns = (projectsUpdatesEnabled: boolean): GroupedGridColDef[] =>
  projectsUpdatesEnabled
    ? DefaultProjectGridColumnsDefs
    : DefaultProjectGridColumnsDefs.filter((col) => col.field !== 'name');

// Project-native fields win on a field-id collision with the Asset catalog (e.g. status,
// nextSteps, supplier.name each exist, same field id, different meaning, in both catalogs).
const projectNativeFieldIds = new Set(DefaultProjectGridColumnsDefs.map((col) => col.field));

let _defaultProjectDetailsGridColumnsDefs: GroupedGridColDef[] | null = null;

/**
 * Projects Details view grid columns — `DefaultProjectGridColumnsDefs` (excluding `details`,
 * the Main Table's row-CTA column, not applicable to the single-project Details view) plus the
 * Inventory/asset field catalog's asset-only fields (e.g. IP Addresses, Access Provider), used
 * for `content.project_details_grid`.
 *
 * Lazily computed, not a top-level constant — see getDefaultProjectAssetDetailFieldDefs' comment
 * for why (this depends on it transitively, so the same circular-import hazard applies here too).
 */
export const getDefaultProjectDetailsGridColumnsDefs = (): GroupedGridColDef[] => {
  if (_defaultProjectDetailsGridColumnsDefs) return _defaultProjectDetailsGridColumnsDefs;
  const assetOnlyFieldDefs = getDefaultProjectAssetDetailFieldDefs().filter(
    (col) => !projectNativeFieldIds.has(col.field)
  );
  _defaultProjectDetailsGridColumnsDefs = [
    ...DefaultProjectGridColumnsDefs.filter((col) => col.field !== 'details'),
    ...assetOnlyFieldDefs
  ];
  return _defaultProjectDetailsGridColumnsDefs;
};

// Catalog for the Projects detail card: the Main Table columns minus the row-CTA `details` column.
// Unlike the table, `name` is always kept — the card is the only place the project's name is shown, so
// the redesign-rollout name-strip (projectGridColumns) must not hide it here. Scoped to the project_grid
// catalog (no asset-only fields); account column visibility is applied by the caller via isCardFieldVisible.
export const projectConfigDetailColumns = (): GroupedGridColDef[] =>
  DefaultProjectGridColumnsDefs.filter((col) => col.field !== 'details');
